import { CheckCircleFilled, SyncOutlined } from "@ant-design/icons";
import { Input, Spin, Typography } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as InstagramIcon } from "assets/images/instagram-icon.svg";
import { ReactComponent as TikTokIcon } from "assets/images/tiktok-icon.svg";
import {
  generateLeadSubmissionAIBrief,
  getLeadSubmissionCollabs,
  updateLeadSubmissionProgress,
} from "services/SauceService";
import {
  trackSelfCampaignDesignContinueClicked,
  trackSelfCampaignDesignProgressSaveFailed,
  trackSelfCampaignDesignProgressSaved,
  trackSelfCampaignDesignStepViewed,
  type SelfCampaignAnalyticsContext,
  type SelfCampaignDesignEventProps,
  type SelfCampaignUserFlow,
} from "analytics";
import {
  LeadSubmission,
  LeadSubmissionCollab,
  LeadSubmissionCollabsFilters,
  LeadSubmissionCollabsResponse,
} from "interfaces/lead-submission";
import { buildSelfCampaignSearchWithId } from "./queryParams";
import { resolveSelfCampaignStepPath } from "./routePaths";
import { useSelfCampaignLead } from "./useSelfCampaignLead";
import {
  SelfServeCampaignBusinessType,
  SelfServeCampaignPlatform,
  SelfServeCampaignPromotionType,
  SelfServeCampaignTone,
} from "./types";
import SelfCampaignPageLayout from "./components/SelfCampaignPageLayout";
import SelfCampaignPrimaryCta from "./components/SelfCampaignPrimaryCta";
import SelfCampaignProgress from "./components/SelfCampaignProgress";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faChevronDown,
  faHashtag,
  faLocationDot,
  faStore,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import LocationFilter from "components/location-filter/LocationFilter";
import "./SelfCampaignDesign.scss";

const { TextArea } = Input;

const TONE_OPTIONS: { value: SelfServeCampaignTone; label: string }[] = [
  { value: "fun", label: "Fun" },
  { value: "premium", label: "Premium" },
  { value: "trendy", label: "Trendy" },
  { value: "family_friendly", label: "Family-friendly" },
];

const BUSINESS_TYPE_LABELS: Record<SelfServeCampaignBusinessType, string> = {
  restaurant_food_beverage: "Restaurant",
  cafe_coffee_shop: "Cafe",
  bar_nightlife: "Bar / Nightlife",
  hotel_resort: "Hotel / Resort",
  travel_tourism: "Travel / Tourism",
  event_venue: "Event Venue",
  beauty_skincare: "Beauty / Skincare",
  fitness_wellness: "Fitness / Wellness",
  fashion_apparel: "Fashion / Apparel",
  retail_local_shop: "Retail",
  consumer_brand: "Consumer Brand",
  e_commerce: "E-commerce",
  entertainment_attractions: "Entertainment",
  arts_culture: "Arts / Culture",
  other: "Local Business",
};

type CreatorPreview = {
  handle: string;
  followers: string;
  badge: string;
  image: string;
  avatar: string;
};

const CREATOR_PREVIEW_SEED: Omit<CreatorPreview, "badge">[] = [
  {
    handle: "foodie_la",
    followers: "28.5K followers",
    image:
      "https://storage.googleapis.com/uxpilot-auth.appspot.com/0318d5ad22-38ee46bb665419929b92.png",
    avatar:
      "https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-1.jpg",
  },
  {
    handle: "la_eats",
    followers: "42.1K followers",
    image:
      "https://storage.googleapis.com/uxpilot-auth.appspot.com/42603e040e-77cc68987fda9892d0ab.png",
    avatar:
      "https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg",
  },
  {
    handle: "tastyla",
    followers: "18.3K followers",
    image:
      "https://storage.googleapis.com/uxpilot-auth.appspot.com/c9e3a25a4d-f7f4987c3ea7ccf9c063.png",
    avatar:
      "https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-5.jpg",
  },
] satisfies Omit<CreatorPreview, "badge">[];

const DEFAULT_CREATOR_COUNT = 493;
const DEFAULT_PROMOTION_TYPE: SelfServeCampaignPromotionType = "hybrid";
const DEFAULT_PLATFORM: SelfServeCampaignPlatform = "instagram_tiktok";
const DEFAULT_TONE: SelfServeCampaignTone = "trendy";

type CreatorPreviewFilterPanel =
  | "location"
  | "platform"
  | "minFollowers"
  | "category"
  | null;

type CreatorPreviewFilters = {
  socialMediaName?: "instagram" | "tiktok";
  minFollowers?: number;
  businessType?: string;
  locations: string[];
};

const DEFAULT_CREATOR_PREVIEW_FILTERS: CreatorPreviewFilters = {
  locations: [],
};

const BUSINESS_TYPE_OPTIONS: Array<{
  value: SelfServeCampaignBusinessType;
  label: string;
}> = Object.entries(BUSINESS_TYPE_LABELS).map(([value, label]) => ({
  value: value as SelfServeCampaignBusinessType,
  label,
}));

const resolveLocationContext = (
  storeAddress?: string,
): { city: string; neighborhood: string } => {
  const normalized = (storeAddress ?? "").trim();
  if (!normalized) {
    return { city: "your city", neighborhood: "your neighborhood" };
  }

  const parts = normalized
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  const city = parts.length >= 3 ? parts[parts.length - 3] : parts[0];
  const neighborhoodCandidate =
    parts.length >= 4 ? parts[parts.length - 4] : parts[0];
  const withoutBusinessPrefix = neighborhoodCandidate.includes(" - ")
    ? neighborhoodCandidate.split(" - ").slice(1).join(" - ").trim()
    : neighborhoodCandidate;
  const neighborhood = /\d/.test(withoutBusinessPrefix)
    ? "your neighborhood"
    : withoutBusinessPrefix || "your neighborhood";

  return {
    city: city || "your city",
    neighborhood,
  };
};

const buildFallbackCreatorPreview = (
  businessTypeLabel: string,
  cityLabel: string,
  neighborhoodLabel: string,
): CreatorPreview[] => {
  const badgeLabels = [
    `Popular with ${cityLabel} ${businessTypeLabel}`,
    `${cityLabel} audience`,
    `${businessTypeLabel} specialist`,
  ];

  return CREATOR_PREVIEW_SEED.map((creator, index) => ({
    ...creator,
    badge: badgeLabels[index % badgeLabels.length],
  }));
};

const formatFollowersCount = (count: number): string => {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1).replace(".0", "")}M followers`;
  }

  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1).replace(".0", "")}K followers`;
  }

  return `${count} followers`;
};

const normalizeCreatorHandle = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return trimmed;
  }

  if (trimmed.startsWith("@")) {
    return trimmed;
  }

  if (/\s/.test(trimmed)) {
    return trimmed;
  }

  return `${trimmed}`;
};

const getPrimaryLocationFilterValue = (
  locations: string[],
): string | undefined => {
  if (!Array.isArray(locations) || locations.length === 0) {
    return undefined;
  }

  const [firstLocation] = locations;
  return typeof firstLocation === "string" && firstLocation.trim().length > 0
    ? firstLocation
    : undefined;
};

const getLocationFilterDisplayValue = (
  locationValue: string | undefined,
  fallback: string,
): string => {
  if (!locationValue) {
    return fallback;
  }

  if (!locationValue.includes("|")) {
    return locationValue;
  }

  const segments = locationValue
    .split("|")
    .map((segment) => segment.trim())
    .filter(Boolean);
  return segments[segments.length - 1] || fallback;
};

const getBusinessTypeLabelFromValue = (businessTypeValue?: string): string => {
  if (!businessTypeValue) {
    return "All";
  }

  if (businessTypeValue in BUSINESS_TYPE_LABELS) {
    return BUSINESS_TYPE_LABELS[
      businessTypeValue as SelfServeCampaignBusinessType
    ];
  }

  return businessTypeValue;
};

const normalizeCreatorPreview = (
  collabs: LeadSubmissionCollab[],
  fallbackPreview: CreatorPreview[],
): CreatorPreview[] => {
  if (collabs.length === 0) {
    return fallbackPreview;
  }

  return collabs.map((collab, index) => {
    const fallback = fallbackPreview[index % fallbackPreview.length];
    const handle = collab.creatorUsername
      ? normalizeCreatorHandle(collab.creatorUsername)
      : fallback.handle;
    const followers =
      typeof collab.followers === "number" && collab.followers > 0
        ? formatFollowersCount(collab.followers)
        : fallback.followers;

    return {
      handle,
      followers,
      badge: fallback.badge,
      image: collab.postCoverImg || fallback.image,
      avatar: collab.avatarUrl || fallback.avatar,
    };
  });
};

const getCreatorCount = (
  payload: LeadSubmissionCollabsResponse,
  fallbackCount: number,
): number => {
  if (
    typeof payload.creatorsAmount === "number" &&
    Number.isFinite(payload.creatorsAmount) &&
    payload.creatorsAmount > 0
  ) {
    return payload.creatorsAmount;
  }

  if (Array.isArray(payload.topCollabs) && payload.topCollabs.length > 0) {
    return payload.topCollabs.length;
  }

  return fallbackCount;
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== "object") {
    return null;
  }

  return value as Record<string, unknown>;
};

const asTrimmedString = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const extractAIBrief = (payload: unknown): string | undefined => {
  const payloadRecord = asRecord(payload);
  const nestedRecord = asRecord(payloadRecord?.data);
  const candidates = [
    payloadRecord?.brief,
    payloadRecord?.generatedBrief,
    payloadRecord?.aiBrief,
    payloadRecord?.text,
    nestedRecord?.brief,
    nestedRecord?.generatedBrief,
    nestedRecord?.aiBrief,
    nestedRecord?.text,
  ];

  for (const candidate of candidates) {
    const resolved = asTrimmedString(candidate);
    if (resolved) {
      return resolved;
    }
  }

  return undefined;
};

const extractBackendSummary = (
  leadSubmission: LeadSubmission | undefined,
): string | undefined => {
  if (!leadSubmission) {
    return undefined;
  }

  const nestedRecord = asRecord(leadSubmission.data);
  const candidates = [
    leadSubmission.summary,
    leadSubmission.businessSummary,
    leadSubmission.description,
    leadSubmission.businessDescription,
    leadSubmission.brief,
    nestedRecord?.summary,
    nestedRecord?.businessSummary,
    nestedRecord?.description,
    nestedRecord?.businessDescription,
    nestedRecord?.brief,
  ];

  for (const candidate of candidates) {
    const resolved = asTrimmedString(candidate);
    if (resolved) {
      return resolved;
    }
  }

  return undefined;
};

const buildDefaultBrief = (businessName?: string): string => {
  const resolvedName = (businessName ?? "").trim() || "your business";
  return `Showcase ${resolvedName}'s unique experience and atmosphere. Highlight what makes your business stand out and attract local creators.`;
};

const normalizePromotionType = (
  value: unknown,
): SelfServeCampaignPromotionType => {
  const normalized = asTrimmedString(value)?.toLowerCase();

  if (
    normalized === "experience_credit" ||
    normalized === "experience-credit" ||
    normalized === "credit"
  ) {
    return "experience_credit";
  }

  return DEFAULT_PROMOTION_TYPE;
};

const normalizePlatform = (value: unknown): SelfServeCampaignPlatform => {
  const normalized = asTrimmedString(value)?.toLowerCase();

  if (
    normalized === "instagram" ||
    normalized === "instagram_only" ||
    normalized === "instagram-only"
  ) {
    return "instagram";
  }

  return DEFAULT_PLATFORM;
};

const mapToneToApiValue = (
  tone: SelfServeCampaignTone,
): "fun" | "premium" | "trendy" | "family-friendly" => {
  if (tone === "family_friendly") {
    return "family-friendly";
  }

  return tone;
};

const resolveSelfCampaignPartner = (
  leadPartner: unknown,
  queryPartner: unknown,
): string | null => {
  const normalizedLeadPartner = asTrimmedString(leadPartner);
  if (normalizedLeadPartner) {
    return normalizedLeadPartner;
  }

  const normalizedQueryPartner = asTrimmedString(queryPartner);
  if (normalizedQueryPartner) {
    return normalizedQueryPartner;
  }

  return null;
};

const resolveSelfCampaignUserFlow = (
  partner: string | null,
): SelfCampaignUserFlow => {
  return partner ? "partner" : "self-campaign";
};

type DesignTrackingPropsInput = {
  promotionType: SelfServeCampaignPromotionType;
  platform: SelfServeCampaignPlatform;
  brief: string;
};

const buildDesignTrackingProps = ({
  promotionType,
  platform,
  brief,
}: DesignTrackingPropsInput): SelfCampaignDesignEventProps => {
  return {
    campaign_type: promotionType === "hybrid" ? "hybrid" : "credit",
    social_media_target: platform,
    brief_length_chars: brief.trim().length,
  };
};

const SelfCampaignDesign: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    leadSubmission,
    leadSubmissionId,
    loadingLeadSubmission,
    queryParams,
  } = useSelfCampaignLead({
    mode: "required",
  });
  const hasTrackedStepViewedRef = useRef(false);
  const hasRequestedInitialAIBriefRef = useRef(false);
  const { businessName, partner: queryPartner } = queryParams;
  const resolvedPartner = useMemo(() => {
    return resolveSelfCampaignPartner(leadSubmission?.partner, queryPartner);
  }, [leadSubmission?.partner, queryPartner]);
  const userFlow = useMemo(() => {
    return resolveSelfCampaignUserFlow(resolvedPartner);
  }, [resolvedPartner]);
  const analyticsContext = useMemo<SelfCampaignAnalyticsContext>(() => {
    return {
      pathname: location.pathname,
      search: location.search,
      leadSubmissionId: leadSubmissionId ?? null,
      partner: resolvedPartner,
      userFlow,
    };
  }, [
    leadSubmissionId,
    location.pathname,
    location.search,
    resolvedPartner,
    userFlow,
  ]);
  const storeAddress = asTrimmedString(leadSubmission?.address);
  const normalizedBusinessType = asTrimmedString(leadSubmission?.businessType);
  const defaultBusinessTypeFilter = normalizedBusinessType;
  const selectedBusinessType =
    normalizedBusinessType && normalizedBusinessType in BUSINESS_TYPE_LABELS
      ? (normalizedBusinessType as SelfServeCampaignBusinessType)
      : undefined;
  const isFoodAndBeverage = selectedBusinessType === "restaurant_food_beverage";
  const businessTypeLabel = selectedBusinessType
    ? BUSINESS_TYPE_LABELS[selectedBusinessType]
    : "Local Business";
  const { city: cityLabel, neighborhood: neighborhoodLabel } = useMemo(() => {
    return resolveLocationContext(storeAddress);
  }, [storeAddress]);
  const fallbackCreatorPreview = useMemo(() => {
    return buildFallbackCreatorPreview(
      businessTypeLabel,
      cityLabel,
      neighborhoodLabel,
    );
  }, [businessTypeLabel, cityLabel, neighborhoodLabel]);
  const backendSummary = useMemo(() => {
    return extractBackendSummary(leadSubmission ?? undefined);
  }, [leadSubmission]);
  const backendBrief = asTrimmedString(leadSubmission?.brief);
  const defaultBrief = useMemo(() => {
    return (
      backendSummary ||
      buildDefaultBrief(
        businessName || asTrimmedString(leadSubmission?.businessName),
      )
    );
  }, [backendSummary, businessName, leadSubmission?.businessName]);

  const [promotionType, setPromotionType] =
    useState<SelfServeCampaignPromotionType>(DEFAULT_PROMOTION_TYPE);
  const [platform, setPlatform] =
    useState<SelfServeCampaignPlatform>(DEFAULT_PLATFORM);
  const [brief, setBrief] = useState<string>(defaultBrief);
  const [tone, setTone] = useState<SelfServeCampaignTone>(DEFAULT_TONE);
  const [creatorPreview, setCreatorPreview] = useState<CreatorPreview[]>([]);
  const [creatorCount, setCreatorCount] = useState<number | null>(null);
  const [creatorFilters, setCreatorFilters] = useState<CreatorPreviewFilters>({
    ...DEFAULT_CREATOR_PREVIEW_FILTERS,
    businessType: defaultBusinessTypeFilter,
  });
  const [activeCreatorFilterPanel, setActiveCreatorFilterPanel] =
    useState<CreatorPreviewFilterPanel>(null);
  const [loadingNearbyCollabs, setLoadingNearbyCollabs] = useState(true);
  const [loadingAIBrief, setLoadingAIBrief] = useState(false);
  const [savingStepProgress, setSavingStepProgress] = useState(false);
  const [designError, setDesignError] = useState<string | null>(null);
  const latestCollabsRequestIdRef = useRef<number>(0);
  const previousLeadSubmissionIdRef = useRef<string | null>(null);

  const primaryLocationFilterValue = useMemo(() => {
    return getPrimaryLocationFilterValue(creatorFilters.locations);
  }, [creatorFilters.locations]);

  const creatorLocationChipValue = useMemo(() => {
    return getLocationFilterDisplayValue(primaryLocationFilterValue, cityLabel);
  }, [cityLabel, primaryLocationFilterValue]);

  const creatorPlatformChipValue = useMemo(() => {
    if (creatorFilters.socialMediaName === "instagram") {
      return "Instagram";
    }

    if (creatorFilters.socialMediaName === "tiktok") {
      return "TikTok";
    }

    return "All";
  }, [creatorFilters.socialMediaName]);

  const creatorMinFollowersChipValue = useMemo(() => {
    if (
      typeof creatorFilters.minFollowers === "number" &&
      creatorFilters.minFollowers > 0
    ) {
      return `${creatorFilters.minFollowers.toLocaleString()}+`;
    }

    return "Any";
  }, [creatorFilters.minFollowers]);

  const creatorCategoryChipValue = useMemo(() => {
    return getBusinessTypeLabelFromValue(creatorFilters.businessType);
  }, [creatorFilters.businessType]);

  const creatorsHeaderSubtitle = useMemo(() => {
    if (!creatorFilters.businessType) {
      return "Tailored for all categories";
    }

    return `Tailored for ${creatorCategoryChipValue}`;
  }, [creatorCategoryChipValue, creatorFilters.businessType]);

  const hasAppliedCreatorFilters = useMemo(() => {
    return Boolean(
      creatorFilters.socialMediaName ||
      (typeof creatorFilters.minFollowers === "number" &&
        creatorFilters.minFollowers > 0) ||
      creatorFilters.locations.length > 0 ||
      creatorFilters.businessType !== defaultBusinessTypeFilter,
    );
  }, [creatorFilters, defaultBusinessTypeFilter]);

  const collabsFilters = useMemo<
    LeadSubmissionCollabsFilters | undefined
  >(() => {
    const nextFilters: LeadSubmissionCollabsFilters = {};

    if (creatorFilters.socialMediaName) {
      nextFilters.socialMediaName = creatorFilters.socialMediaName;
    }

    if (
      typeof creatorFilters.minFollowers === "number" &&
      creatorFilters.minFollowers > 0
    ) {
      nextFilters.minFollowers = creatorFilters.minFollowers;
    }

    if (primaryLocationFilterValue) {
      nextFilters.location = primaryLocationFilterValue;
    }

    if (creatorFilters.businessType) {
      nextFilters.businessType = creatorFilters.businessType;
    }

    return Object.keys(nextFilters).length > 0 ? nextFilters : undefined;
  }, [creatorFilters, primaryLocationFilterValue]);

  const requestAIBrief = useCallback(
    async (
      id: string,
      selectedTone: SelfServeCampaignTone,
    ): Promise<string | undefined> => {
      try {
        setLoadingAIBrief(true);
        setDesignError(null);

        const response = await generateLeadSubmissionAIBrief(id, {
          tone: mapToneToApiValue(selectedTone),
        });

        const generatedBrief = extractAIBrief(response);

        if (!generatedBrief) {
          setDesignError("AI could not generate a brief right now.");
          return undefined;
        }

        setBrief(generatedBrief);
        return generatedBrief;
      } catch (_error) {
        setDesignError("AI could not generate a brief right now.");
        return undefined;
      } finally {
        setLoadingAIBrief(false);
      }
    },
    [],
  );

  const toggleCreatorFilterPanel = useCallback(
    (panel: Exclude<CreatorPreviewFilterPanel, null>) => {
      setActiveCreatorFilterPanel((prevPanel) => {
        return prevPanel === panel ? null : panel;
      });
    },
    [],
  );

  const onPlatformFilterChange = useCallback(
    (nextPlatform?: "instagram" | "tiktok") => {
      setCreatorFilters((prevFilters) => ({
        ...prevFilters,
        socialMediaName: nextPlatform,
      }));
    },
    [],
  );

  const onMinFollowersFilterChange = useCallback((nextValue: string) => {
    const parsedValue = Number(nextValue);
    const sanitizedValue =
      Number.isFinite(parsedValue) && parsedValue > 0
        ? Math.floor(parsedValue)
        : undefined;

    setCreatorFilters((prevFilters) => ({
      ...prevFilters,
      minFollowers: sanitizedValue,
    }));
  }, []);

  const onCategoryFilterChange = useCallback((nextCategoryValue: string) => {
    const normalizedCategoryValue = nextCategoryValue.trim();
    setCreatorFilters((prevFilters) => ({
      ...prevFilters,
      businessType: normalizedCategoryValue || undefined,
    }));
  }, []);

  const onLocationFilterChange = useCallback((nextLocations: unknown) => {
    if (!Array.isArray(nextLocations)) {
      setCreatorFilters((prevFilters) => ({
        ...prevFilters,
        locations: [],
      }));
      return;
    }

    const normalizedLocations = nextLocations
      .filter((locationValue): locationValue is string => {
        return (
          typeof locationValue === "string" && locationValue.trim().length > 0
        );
      })
      .map((locationValue) => locationValue.trim());

    const latestLocation =
      normalizedLocations.length > 0
        ? normalizedLocations[normalizedLocations.length - 1]
        : undefined;

    setCreatorFilters((prevFilters) => ({
      ...prevFilters,
      locations: latestLocation ? [latestLocation] : [],
    }));
  }, []);

  const onClearCreatorFilters = useCallback(() => {
    setCreatorFilters({
      ...DEFAULT_CREATOR_PREVIEW_FILTERS,
      businessType: defaultBusinessTypeFilter,
    });
    setActiveCreatorFilterPanel(null);
  }, [defaultBusinessTypeFilter]);

  useEffect(() => {
    if (!leadSubmission) {
      return;
    }

    setPromotionType(normalizePromotionType(leadSubmission.campaignType));
    setPlatform(normalizePlatform(leadSubmission.socialMediaTarget));
    setBrief(backendBrief || defaultBrief);
  }, [backendBrief, defaultBrief, leadSubmission]);

  useEffect(() => {
    const normalizedLeadSubmissionId = leadSubmissionId ?? null;

    if (previousLeadSubmissionIdRef.current === normalizedLeadSubmissionId) {
      return;
    }

    previousLeadSubmissionIdRef.current = normalizedLeadSubmissionId;
    setActiveCreatorFilterPanel(null);
    setCreatorFilters((prevFilters) => {
      if (
        !prevFilters.socialMediaName &&
        (!prevFilters.minFollowers || prevFilters.minFollowers <= 0) &&
        prevFilters.locations.length === 0 &&
        prevFilters.businessType === defaultBusinessTypeFilter
      ) {
        return prevFilters;
      }

      return {
        ...DEFAULT_CREATOR_PREVIEW_FILTERS,
        businessType: defaultBusinessTypeFilter,
      };
    });
  }, [defaultBusinessTypeFilter, leadSubmissionId]);

  useEffect(() => {
    if (loadingLeadSubmission || hasRequestedInitialAIBriefRef.current) {
      return;
    }

    if (!leadSubmissionId || backendBrief) {
      return;
    }

    hasRequestedInitialAIBriefRef.current = true;
    void requestAIBrief(leadSubmissionId, DEFAULT_TONE);
  }, [backendBrief, leadSubmissionId, loadingLeadSubmission, requestAIBrief]);

  useEffect(() => {
    let isMounted = true;

    const loadNearbyCollabs = async () => {
      if (!leadSubmissionId) {
        if (isMounted && !loadingLeadSubmission) {
          setLoadingNearbyCollabs(false);
        }
        return;
      }

      const requestId = ++latestCollabsRequestIdRef.current;

      try {
        setLoadingNearbyCollabs(true);
        setDesignError(null);
        const response = await getLeadSubmissionCollabs(
          leadSubmissionId,
          collabsFilters,
        );

        if (!isMounted || latestCollabsRequestIdRef.current !== requestId) {
          return;
        }

        const topCollabs = Array.isArray(response.topCollabs)
          ? response.topCollabs
          : [];
        const hasTopCollabs = topCollabs.length > 0;

        setCreatorPreview(
          hasTopCollabs
            ? normalizeCreatorPreview(topCollabs, fallbackCreatorPreview)
            : fallbackCreatorPreview,
        );
        setCreatorCount(getCreatorCount(response, DEFAULT_CREATOR_COUNT));
      } catch (_error) {
        if (isMounted && latestCollabsRequestIdRef.current === requestId) {
          setCreatorPreview([]);
          setCreatorCount(null);
          setDesignError("Could not load nearby collabs right now.");
        }
      } finally {
        if (isMounted && latestCollabsRequestIdRef.current === requestId) {
          setLoadingNearbyCollabs(false);
        }
      }
    };

    void loadNearbyCollabs();

    return () => {
      isMounted = false;
    };
  }, [
    collabsFilters,
    fallbackCreatorPreview,
    leadSubmissionId,
    loadingLeadSubmission,
  ]);

  useEffect(() => {
    if (loadingLeadSubmission || hasTrackedStepViewedRef.current) {
      return;
    }

    hasTrackedStepViewedRef.current = true;
    trackSelfCampaignDesignStepViewed(
      analyticsContext,
      buildDesignTrackingProps({ promotionType, platform, brief }),
    );
  }, [analyticsContext, brief, loadingLeadSubmission, platform, promotionType]);

  const canContinue = brief.trim().length > 0 && Boolean(leadSubmissionId);

  const onContinue = async () => {
    const trackingProps = buildDesignTrackingProps({
      promotionType,
      platform,
      brief,
    });
    trackSelfCampaignDesignContinueClicked(analyticsContext, trackingProps);

    if (!leadSubmissionId) {
      return;
    }

    const normalizedBrief = brief.trim();

    if (!normalizedBrief) {
      return;
    }

    try {
      setDesignError(null);
      setSavingStepProgress(true);

      await updateLeadSubmissionProgress(leadSubmissionId, {
        step: 2,
        campaignType: promotionType === "hybrid" ? "hybrid" : "credit",
        socialMediaTarget: platform,
        brief: normalizedBrief,
      });
      trackSelfCampaignDesignProgressSaved(analyticsContext, trackingProps);
    } catch (_error) {
      trackSelfCampaignDesignProgressSaveFailed(
        analyticsContext,
        trackingProps,
      );
      setDesignError("We could not save your progress. Please try again.");
      setSavingStepProgress(false);
      return;
    }

    navigate({
      pathname: resolveSelfCampaignStepPath(location.pathname, "activate"),
      search: buildSelfCampaignSearchWithId(location.search, leadSubmissionId),
    });

    setSavingStepProgress(false);
  };

  const onRegenerateBrief = async () => {
    if (!leadSubmissionId) {
      return;
    }

    await requestAIBrief(leadSubmissionId, tone);
  };

  if (loadingLeadSubmission) {
    return (
      <div className="self-campaign-create__full-page-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <SelfCampaignPageLayout
      rootClassName="self-campaign-design"
      variant="design"
      leadPartner={leadSubmission?.partner}
      footer={
        <SelfCampaignPrimaryCta
          label="Continue to Activate"
          mode="responsive-footer"
          onClick={onContinue}
          disabled={!canContinue || savingStepProgress}
        />
      }
    >
      <SelfCampaignProgress
        title="Design your campaign"
        activeStep={3}
        variant="design"
      />

      <section className="self-campaign-design__creators-card">
        <div className="self-campaign-design__creators-header">
          <h2>
            {loadingNearbyCollabs
              ? "Loading creators..."
              : `${creatorCount ?? DEFAULT_CREATOR_COUNT} Local creators in ${cityLabel}`}
          </h2>
          <p>{creatorsHeaderSubtitle}</p>
        </div>
        <div className="self-campaign-design__filters-shell">
          <div className="self-campaign-design__filters-row">
            <button
              type="button"
              className={`self-campaign-design__filter-chip ${
                activeCreatorFilterPanel === "location" ? "active" : ""
              }`}
              onClick={() => toggleCreatorFilterPanel("location")}
              data-testid="creator-filter-chip-location"
            >
              <FontAwesomeIcon icon={faLocationDot} />
              <span>{`Location: ${creatorLocationChipValue}`}</span>
              <FontAwesomeIcon
                icon={faChevronDown}
                className="self-campaign-design__filter-chip-chevron"
              />
            </button>
            <button
              type="button"
              className={`self-campaign-design__filter-chip ${
                activeCreatorFilterPanel === "category" ? "active" : ""
              }`}
              onClick={() => toggleCreatorFilterPanel("category")}
              data-testid="creator-filter-chip-category"
            >
              <FontAwesomeIcon icon={faStore} />
              <span>{`Category: ${creatorCategoryChipValue}`}</span>
              <FontAwesomeIcon
                icon={faChevronDown}
                className="self-campaign-design__filter-chip-chevron"
              />
            </button>
            <button
              type="button"
              className={`self-campaign-design__filter-chip ${
                activeCreatorFilterPanel === "platform" ? "active" : ""
              }`}
              onClick={() => toggleCreatorFilterPanel("platform")}
              data-testid="creator-filter-chip-platform"
            >
              <FontAwesomeIcon icon={faHashtag} />
              <span>{`Platform: ${creatorPlatformChipValue}`}</span>
              <FontAwesomeIcon
                icon={faChevronDown}
                className="self-campaign-design__filter-chip-chevron"
              />
            </button>
            <button
              type="button"
              className={`self-campaign-design__filter-chip ${
                activeCreatorFilterPanel === "minFollowers" ? "active" : ""
              }`}
              onClick={() => toggleCreatorFilterPanel("minFollowers")}
              data-testid="creator-filter-chip-min-followers"
            >
              <FontAwesomeIcon icon={faUsers} />
              <span>{`Min: ${creatorMinFollowersChipValue}`}</span>
              <FontAwesomeIcon
                icon={faChevronDown}
                className="self-campaign-design__filter-chip-chevron"
              />
            </button>
            {hasAppliedCreatorFilters ? (
              <button
                type="button"
                className="self-campaign-design__filter-clear-btn"
                onClick={onClearCreatorFilters}
                data-testid="creator-filter-clear"
              >
                Clear
              </button>
            ) : null}
          </div>
          {activeCreatorFilterPanel ? (
            <div className="self-campaign-design__filters-panel">
              {activeCreatorFilterPanel === "location" ? (
                <div className="self-campaign-design__filter-panel-section">
                  <p className="self-campaign-design__filter-panel-copy">
                    Choose a location using the same creators-page format.
                  </p>
                  <LocationFilter
                    onChangeValues={onLocationFilterChange}
                    values={creatorFilters.locations}
                  />
                </div>
              ) : null}
              {activeCreatorFilterPanel === "platform" ? (
                <div className="self-campaign-design__filter-panel-section">
                  <p className="self-campaign-design__filter-panel-copy">
                    Applied automatically when you choose an option.
                  </p>
                  <div className="self-campaign-design__filter-option-grid">
                    <button
                      type="button"
                      className={`self-campaign-design__filter-option ${
                        !creatorFilters.socialMediaName ? "active" : ""
                      }`}
                      onClick={() => onPlatformFilterChange(undefined)}
                      data-testid="creator-filter-option-platform-all"
                    >
                      All
                    </button>
                    <button
                      type="button"
                      className={`self-campaign-design__filter-option ${
                        creatorFilters.socialMediaName === "instagram"
                          ? "active"
                          : ""
                      }`}
                      onClick={() => onPlatformFilterChange("instagram")}
                      data-testid="creator-filter-option-platform-instagram"
                    >
                      Instagram
                    </button>
                    <button
                      type="button"
                      className={`self-campaign-design__filter-option ${
                        creatorFilters.socialMediaName === "tiktok"
                          ? "active"
                          : ""
                      }`}
                      onClick={() => onPlatformFilterChange("tiktok")}
                      data-testid="creator-filter-option-platform-tiktok"
                    >
                      TikTok
                    </button>
                  </div>
                </div>
              ) : null}
              {activeCreatorFilterPanel === "minFollowers" ? (
                <div className="self-campaign-design__filter-panel-section">
                  <label
                    htmlFor="creator-min-followers-input"
                    className="self-campaign-design__filter-label"
                  >
                    Minimum followers
                  </label>
                  <input
                    id="creator-min-followers-input"
                    className="self-campaign-design__filter-input"
                    type="number"
                    min={0}
                    step={1000}
                    value={creatorFilters.minFollowers ?? ""}
                    onChange={(event) =>
                      onMinFollowersFilterChange(event.target.value)
                    }
                    placeholder="e.g. 5000"
                    data-testid="creator-filter-option-min-followers-input"
                  />
                  <p className="self-campaign-design__filter-panel-copy">
                    Updates the preview instantly. Use 0 or empty to clear.
                  </p>
                </div>
              ) : null}
              {activeCreatorFilterPanel === "category" ? (
                <div className="self-campaign-design__filter-panel-section">
                  <p className="self-campaign-design__filter-panel-copy">
                    Applied automatically when you choose a category.
                  </p>
                  <label
                    htmlFor="creator-category-select"
                    className="self-campaign-design__filter-label"
                  >
                    Category
                  </label>
                  <select
                    id="creator-category-select"
                    className="self-campaign-design__filter-input"
                    value={creatorFilters.businessType ?? ""}
                    onChange={(event) =>
                      onCategoryFilterChange(event.target.value)
                    }
                    data-testid="creator-filter-option-category-select"
                  >
                    <option value="">All categories</option>
                    {BUSINESS_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="self-campaign-design__creators-list">
          {loadingNearbyCollabs ? (
            <div className="self-campaign-design__creators-loading">
              <div className="self-campaign-design__creator-skeleton" />
              <div className="self-campaign-design__creator-skeleton" />
              <div className="self-campaign-design__creator-skeleton" />
              <div className="self-campaign-design__creator-more self-campaign-design__creator-more--skeleton" />
            </div>
          ) : (
            <>
              {creatorPreview.map((creator) => (
                <article
                  key={creator.handle}
                  className="self-campaign-design__creator-item"
                >
                  <img
                    src={creator.image}
                    alt={creator.handle}
                    className="self-campaign-design__creator-cover"
                  />
                  <div className="self-campaign-design__creator-overlay" />
                  {/* <span className="self-campaign-design__creator-badge">
                    {creator.badge}
                  </span> */}
                  <div className="self-campaign-design__creator-footer">
                    <img src={creator.avatar} alt={creator.handle} />
                    <div>
                      <p>{creator.handle}</p>
                      <span>{creator.followers}</span>
                    </div>
                  </div>
                </article>
              ))}
              {creatorPreview.length > 0 ? (
                <div className="self-campaign-design__creator-more">
                  <FontAwesomeIcon icon={faArrowRight} />
                  <p>Activate to see more</p>
                </div>
              ) : (
                <Typography.Text className="self-campaign-design__creators-empty">
                  No creators available right now.
                </Typography.Text>
              )}
            </>
          )}
        </div>
      </section>

      {designError ? (
        <Typography.Text
          type="danger"
          className="self-campaign-design__status-message"
        >
          {designError}
        </Typography.Text>
      ) : null}

      <section className="self-campaign-design__section">
        <div className="self-campaign-design__section-heading">
          <h3>
            How would you like creators to promote your {businessTypeLabel}?
          </h3>
          <p>
            The more you offer, the more creators apply and the more buzz you
            generate.
          </p>
        </div>
        <div className="self-campaign-design__promotion-grid">
          <button
            type="button"
            className={`self-campaign-design__promotion-card ${
              promotionType === "experience_credit" ? "selected" : ""
            }`}
            onClick={() => setPromotionType("experience_credit")}
          >
            <h4>Experience Credit</h4>
            <span className="chip">Lower Cost</span>
            <p>
              {isFoodAndBeverage
                ? "Invite creators to enjoy a dining experience in exchange for content."
                : "Invite creators to enjoy an experience in exchange for content."}
            </p>
            <div className="price-box">
              <small>Experience Credit</small>
              <strong>$75-$250</strong>
            </div>
            <ul>
              <li>Lower cost option</li>
              <li>Good for basic exposure</li>
            </ul>
          </button>

          <button
            type="button"
            className={`self-campaign-design__promotion-card self-campaign-design__promotion-card--featured ${
              promotionType === "hybrid" ? "selected" : ""
            }`}
            onClick={() => setPromotionType("hybrid")}
          >
            <span className="top-chip">Most Popular</span>
            <h4>Hybrid</h4>
            <span className="chip chip--primary">Best Results</span>
            <p>
              {isFoodAndBeverage
                ? "Combine dining experience + cash incentive for better creators and stronger results."
                : "Combine Experience Credit + cash incentive for better creators and stronger results."}
            </p>
            <div className="price-box price-box--split">
              <div className="price-box__offer price-box__offer--left">
                <small>Experience Credit</small>
                <strong>$75-$250</strong>
              </div>
              <span className="price-box__plus">+</span>
              <div className="price-box__offer price-box__offer--right">
                <small>Cash incentive</small>
                <strong>$100-$250</strong>
              </div>
            </div>
            <ul>
              <li>More creator applications</li>
              <li>Higher quality content</li>
              <li>Greater buzz and reach</li>
            </ul>
            <div className="insight">
              3x more creator applications on average
            </div>
          </button>
        </div>
      </section>

      <section className="self-campaign-design__section">
        <div className="self-campaign-design__section-heading">
          <h3>Where should this run?</h3>
        </div>
        <div className="self-campaign-design__platform-grid">
          <button
            type="button"
            className={`self-campaign-design__platform-card ${
              platform === "instagram" ? "selected" : ""
            }`}
            onClick={() => setPlatform("instagram")}
          >
            <div className="icon-wrap icon-wrap--instagram">
              <InstagramIcon />
            </div>
            <div>
              <h4>Instagram Only</h4>
              <p>Stories and Reels</p>
            </div>
            <small>Best for visual storytelling</small>
          </button>

          <button
            type="button"
            className={`self-campaign-design__platform-card ${
              platform === "instagram_tiktok" ? "selected" : ""
            }`}
            onClick={() => setPlatform("instagram_tiktok")}
          >
            <span className="top-chip">Popular</span>
            <div className="icon-wrap icon-wrap--combo">
              <InstagramIcon />
              <TikTokIcon />
            </div>
            <div>
              <h4>Instagram / TikTok</h4>
              <p>Maximum reach</p>
            </div>
            <small>2x platform exposure and diverse audience</small>
          </button>
        </div>
      </section>

      <section className="self-campaign-design__section">
        <div className="self-campaign-design__section-heading self-campaign-design__section-heading--row">
          <h3>Campaign Brief</h3>
          <button
            type="button"
            className="self-campaign-design__ai-btn"
            onClick={onRegenerateBrief}
            disabled={loadingAIBrief || !leadSubmissionId}
          >
            <SyncOutlined />
            {loadingAIBrief ? "Generating..." : "Regenerate with AI"}
          </button>
        </div>
        <TextArea
          value={brief}
          onChange={(event) => setBrief(event.target.value)}
          rows={5}
        />
        <div className="self-campaign-design__tone-row">
          <span>Tone:</span>
          {TONE_OPTIONS.map((toneOption) => (
            <button
              key={toneOption.value}
              type="button"
              onClick={() => setTone(toneOption.value)}
              className={`self-campaign-design__tone-btn ${
                tone === toneOption.value ? "active" : ""
              }`}
            >
              {toneOption.label}
            </button>
          ))}
        </div>
      </section>

      <section className="self-campaign-design__summary">
        <CheckCircleFilled />
        <Typography.Text>
          Campaign for{" "}
          <strong>
            {businessName ||
              asTrimmedString(leadSubmission?.businessName) ||
              "your business"}
          </strong>{" "}
          is ready for activation.
        </Typography.Text>
      </section>
    </SelfCampaignPageLayout>
  );
};

export default SelfCampaignDesign;
